import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import { motion, useMotionValue, PanInfo } from "framer-motion";
import { NodeConfig } from "../../types/algoStudio";

interface NodeWrapperProps {
  isSelected: boolean;
  isMultiColumn?: boolean;
}

const NodeWrapper = styled(motion.div) <NodeWrapperProps>`
  position: absolute;
  width: ${(props) => (props.isMultiColumn ? "500px" : "250px")};
  max-width: ${(props) => (props.isMultiColumn ? "500px" : "250px")};
  cursor: grab;
  user-select: none;
  border: 1px solid
    ${(props: NodeWrapperProps) =>
    props.isSelected ? "#6b9fff" : "#252a38"};
  background: #11131a;
  border-radius: 8px;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 100px;
  max-height: 500px;
  
  &:hover {
    border-color: #3a4258;
  }
  &:active {
    cursor: grabbing;
  }
`;

const NodeHeader = styled.div`
  background-color: #181b24;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #e8eaed;
  border-bottom: 1px solid #252a38;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const NodeBody = styled.div`
  padding: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #11131a;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #252a38;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #3a4258;
  }
`;

const Column = styled.div`
  flex: 1;
  min-width: 200px;
  max-width: 100%;
  overflow: hidden;
`;

const ConfigItem = styled.div`
  margin-bottom: 8px;
  font-size: 0.85rem;
  overflow: hidden;
  word-wrap: break-word;
  word-break: break-word;

  .label {
    color: #8b919a;
    font-size: 0.75rem;
    margin-bottom: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .value {
    color: #e8eaed;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
    line-height: 1.4;
    max-width: 100%;
    
    /* Allow wrapping for long values but truncate if too long */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .sub-value {
    color: #8b919a;
    font-size: 0.8rem;
    margin-left: 8px;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
`;

const SectionTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #8b919a;
  margin: 12px 0 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #252a38;
`;

interface NodeProps {
  node: NodeConfig;
  isSelected: boolean;
  onClick: () => void;
  onMove: (nodeId: string, x: number, y: number) => void;
}

const Node: React.FC<NodeProps> = ({ node, isSelected, onClick, onMove }) => {
  const x = useMotionValue(node.position.x);
  const y = useMotionValue(node.position.y);
  const scale = useMotionValue(1);
  const opacity = useMotionValue(1);
  const isDragging = useRef(false);

  // Update position when node position changes (but not during drag)
  useEffect(() => {
    if (!isDragging.current) {
      x.set(node.position.x);
      y.set(node.position.y);
    }
  }, [node.position.x, node.position.y, x, y]);

  const handleDragStart = () => {
    isDragging.current = true;
    scale.set(1.05);
    opacity.set(0.8);
  };

  const handleDragEnd = () => {
    isDragging.current = false;
    scale.set(1);
    opacity.set(1);
  };

  const handleDrag = (_: any, info: PanInfo) => {
    const newX = x.get() + info.delta.x;
    const newY = y.get() + info.delta.y;
    x.set(newX);
    y.set(newY);
    onMove(node.id, newX, newY);
  };

  const isMultiColumnNode =
    node.type === "Algorithm" || node.type === "Trade Execution and Management";

  const renderConfigContent = () => {
    switch (node.type) {
      case "Algorithm":
        return (
          <>
            <Column>
              <ConfigItem>
                <div className="label">Name</div>
                <div className="value">{node.config.name || "Unnamed"}</div>
              </ConfigItem>
              {node.config.description && (
                <ConfigItem>
                  <div className="label">Description</div>
                  <div className="value" style={{ fontSize: "0.75rem" }}>
                    {node.config.description}
                  </div>
                </ConfigItem>
              )}
              <ConfigItem>
                <div className="label">Timeframe</div>
                <div className="value">{node.config.timeframe || "1h"}</div>
              </ConfigItem>

              <SectionTitle>Moving Averages</SectionTitle>
              {node.config.movingAverages?.length > 0 ? (
                node.config.movingAverages.map((ma: any, index: number) => (
                  <ConfigItem key={index}>
                    <div className="label">MA {index + 1}</div>
                    <div className="value">
                      {ma.type} ({ma.period})
                      <span className="sub-value">
                        {ma.priceType}
                      </span>
                    </div>
                  </ConfigItem>
                ))
              ) : (
                <div className="sub-value" style={{ fontSize: "0.75rem" }}>
                  No indicators configured
                </div>
              )}
            </Column>

            <Column>
              <SectionTitle>Entry Conditions</SectionTitle>
              <ConfigItem>
                <div className="label">Buy Conditions</div>
                <div className="value">
                  {node.config.entryConditions?.buyConditions?.length > 0 ? (
                    node.config.entryConditions.buyConditions.map(
                      (condition: any, index: number) => (
                        <div key={index} className="sub-value">
                          {condition.indicator1?.type} {condition.indicator1?.period}{" "}
                          {condition.relationship}{" "}
                          {condition.indicator2?.type} {condition.indicator2?.period}
                        </div>
                      )
                    )
                  ) : (
                    <span className="sub-value">None</span>
                  )}
                </div>
              </ConfigItem>
              <ConfigItem>
                <div className="label">Sell Conditions</div>
                <div className="value">
                  {node.config.entryConditions?.sellConditions?.length > 0 ? (
                    node.config.entryConditions.sellConditions.map(
                      (condition: any, index: number) => (
                        <div key={index} className="sub-value">
                          {condition.indicator1?.type} {condition.indicator1?.period}{" "}
                          {condition.relationship}{" "}
                          {condition.indicator2?.type} {condition.indicator2?.period}
                        </div>
                      )
                    )
                  ) : (
                    <span className="sub-value">None</span>
                  )}
                </div>
              </ConfigItem>

              <SectionTitle>Exit Conditions</SectionTitle>
              <ConfigItem>
                <div className="label">Buy Exits</div>
                <div className="value">
                  {node.config.exitConditions?.buyExitConditions?.length > 0 ? (
                    node.config.exitConditions.buyExitConditions.map(
                      (condition: any, index: number) => (
                        <div key={index} className="sub-value">
                          {condition.indicator1?.type} {condition.indicator1?.period}{" "}
                          {condition.relationship}{" "}
                          {condition.indicator2?.type} {condition.indicator2?.period}
                        </div>
                      )
                    )
                  ) : (
                    <span className="sub-value">None</span>
                  )}
                </div>
              </ConfigItem>
              <ConfigItem>
                <div className="label">Sell Exits</div>
                <div className="value">
                  {node.config.exitConditions?.sellExitConditions?.length > 0 ? (
                    node.config.exitConditions.sellExitConditions.map(
                      (condition: any, index: number) => (
                        <div key={index} className="sub-value">
                          {condition.indicator1?.type} {condition.indicator1?.period}{" "}
                          {condition.relationship}{" "}
                          {condition.indicator2?.type} {condition.indicator2?.period}
                        </div>
                      )
                    )
                  ) : (
                    <span className="sub-value">None</span>
                  )}
                </div>
              </ConfigItem>
            </Column>
          </>
        );
      case "Trade Execution and Management":
        return (
          <>
            <Column>
              <ConfigItem>
                <div className="label">Lot Mode</div>
                <div className="value">{node.config.lotMode || "Fixed"}</div>
              </ConfigItem>


              <SectionTitle>Take Profit & Stop Loss</SectionTitle>
              <ConfigItem>
                <div className="label">TP / SL</div>
                <div className="value">
                  {node.config.takeProfitPercentage || 0}% / {node.config.stopLossPercentage || 0}%
                </div>
              </ConfigItem>
              {node.config.breakevenAfterProfit > 0 && (
                <ConfigItem>
                  <div className="label">Breakeven</div>
                  <div className="value">After ${node.config.breakevenAfterProfit}</div>
                </ConfigItem>
              )}

              <SectionTitle>Trailing Settings</SectionTitle>
              <ConfigItem>
                <div className="label">Trailing Stop</div>
                <div className="value">
                  {node.config.trailingStopEnabled ? "Enabled" : "Disabled"}
                  {node.config.trailingStopEnabled && (
                    <span className="sub-value">
                      {node.config.trailingStopType}: {node.config.trailingStopAmount}%
                    </span>
                  )}
                </div>
              </ConfigItem>
              <ConfigItem>
                <div className="label">Trailing TP</div>
                <div className="value">
                  {node.config.trailingTakeProfitEnabled ? "Enabled" : "Disabled"}
                  {node.config.trailingTakeProfitEnabled && (
                    <span className="sub-value">
                      {node.config.trailingTakeProfitType}: {node.config.trailingTakeProfitAmount}%
                    </span>
                  )}
                </div>
              </ConfigItem>
            </Column>

            <Column>
              <SectionTitle>Execution Settings</SectionTitle>
              <ConfigItem>
                <div className="label">Order Type</div>
                <div className="value">{node.config.orderType || "market"}</div>
              </ConfigItem>
              <ConfigItem>
                <div className="label">Max Orders</div>
                <div className="value">{node.config.maxOrders || 10}</div>
              </ConfigItem>
              <ConfigItem>
                <div className="label">Stack Every Bar</div>
                <div className="value">
                  {node.config.stackEntriesEveryBar !== false ? "Enabled" : "Disabled"}
                </div>
              </ConfigItem>
              <ConfigItem>
                <div className="label">Pyramiding</div>
                <div className="value">
                  {node.config.allowPyramiding ? "Allowed" : "Not Allowed"}
                  {node.config.allowPyramiding && (
                    <span className="sub-value">
                      Max: {node.config.maxPyramidPositions}
                      {node.config.pyramidSize && <span> (Size: {node.config.pyramidSize})</span>}
                    </span>
                  )}
                </div>
              </ConfigItem>
              <ConfigItem>
                <div className="label">Hedging</div>
                <div className="value">
                  {node.config.allowHedging !== false ? "Allowed" : "Not Allowed"}
                </div>
              </ConfigItem>
              <ConfigItem>
                <div className="label">Close on Bias Flip</div>
                <div className="value">
                  {node.config.closeOnBiasFlip ? "Enabled" : "Disabled"}
                </div>
              </ConfigItem>
              <ConfigItem>
                <div className="label">Reversals</div>
                <div className="value">
                  {node.config.allowReversal ? "Allowed" : "Not Allowed"}
                </div>
              </ConfigItem>

              <SectionTitle>Additional Settings</SectionTitle>
              <ConfigItem>
                <div className="label">Max Drawdown</div>
                <div className="value">
                  {node.config.maxDrawdownPercentage || 0}%
                  {node.config.maxDrawdown && (
                    <span className="sub-value">
                      (${node.config.maxDrawdown})
                    </span>
                  )}
                </div>
              </ConfigItem>
              {(node.config.limitPriceOffset > 0 || node.config.stopPriceOffset > 0) && (
                <ConfigItem>
                  <div className="label">Order Offsets</div>
                  <div className="value" style={{ fontSize: "0.75rem" }}>
                    {node.config.limitPriceOffset > 0 && <div>Limit: {node.config.limitPriceOffset}</div>}
                    {node.config.stopPriceOffset > 0 && <div>Stop: {node.config.stopPriceOffset}</div>}
                  </div>
                </ConfigItem>
              )}
              {node.config.closeTradeAfterDrawdown > 0 && (
                <ConfigItem>
                  <div className="label">Close After Drawdown</div>
                  <div className="value">
                    ${node.config.closeTradeAfterDrawdown}
                  </div>
                </ConfigItem>
              )}
              <ConfigItem>
                <div className="label">Intrabar Calculation</div>
                <div className="value">
                  {node.config.useIntrabarCalculation ? "Enabled" : "Disabled"}
                </div>
              </ConfigItem>
            </Column>
          </>
        );
      case "Symbol":
        return (
          <>
            <ConfigItem>
              <div className="label">Symbol</div>
              <div className="value">{node.config.symbol || "Not set"}</div>
            </ConfigItem>
            <ConfigItem>
              <div className="label">Asset Class</div>
              <div className="value">{node.config.assetClass || "Stocks"}</div>
            </ConfigItem>
            <ConfigItem>
              <div className="label">Max Capital</div>
              <div className="value">${node.config.maxCapital || 0}</div>
            </ConfigItem>
            {node.config.takeProfitValue > 0 && (
              <ConfigItem>
                <div className="label">Take Profit</div>
                <div className="value">{node.config.takeProfitValue}</div>
              </ConfigItem>
            )}
            {node.config.stopLossValue > 0 && (
              <ConfigItem>
                <div className="label">Stop Loss</div>
                <div className="value">{node.config.stopLossValue}</div>
              </ConfigItem>
            )}
            {node.config.maxRiskValue > 0 && (
              <ConfigItem>
                <div className="label">Max Risk</div>
                <div className="value">
                  {node.config.maxRiskValue}
                  {node.config.maxRiskMode === "percentage" ? "%" : "$"}
                </div>
              </ConfigItem>
            )}
            {/* P&L Summary */}
            {(node.config.pipSize || node.config.contractMultiplier || node.config.pnlMultiplier) && (
              <ConfigItem>
                <div className="label">P&L Config</div>
                <div className="value" style={{ fontSize: "0.75rem" }}>
                  {node.config.pipSize && <div>Point Size: {node.config.pipSize}</div>}
                  {node.config.contractMultiplier && <div>Mult: {node.config.contractMultiplier}</div>}
                  {node.config.pnlMultiplier && <div>P&L Mult: {node.config.pnlMultiplier}</div>}
                  {node.config.quantityContracts && <div>Qty: {node.config.quantityContracts}</div>}
                </div>
              </ConfigItem>
            )}
          </>
        );
      case "Schedule":
        return (
          <>
            <ConfigItem>
              <div className="label">Active Shutdown</div>
              <div className="value">
                {node.config.activeShutdown ? "Enabled" : "Disabled"}
              </div>
            </ConfigItem>
            {node.config.activeShutdown && (
              <>
                <ConfigItem>
                  <div className="label">Close</div>
                  <div className="value">
                    {node.config.closeDay} at {node.config.closeTime}
                  </div>
                </ConfigItem>
                <ConfigItem>
                  <div className="label">Restart</div>
                  <div className="value">
                    {node.config.restartDay} at {node.config.restartTime}
                  </div>
                </ConfigItem>
              </>
            )}
            <ConfigItem>
              <div className="label">Daily Start</div>
              <div className="value">{node.config.dailyStartTime || "09:30"}</div>
            </ConfigItem>
            <ConfigItem>
              <div className="label">Daily Stop</div>
              <div className="value">{node.config.dailyStopTime || "16:00"}</div>
            </ConfigItem>
          </>
        );
      case "Stocks":
      case "Stock Options":
      case "Futures":
      case "Futures Options":
      case "Forex":
      case "Crypto Spot":
      case "Crypto Futures":
        // Asset class components
        return (
          <>
            <ConfigItem>
              <div className="label">Type</div>
              <div className="value">{node.type}</div>
            </ConfigItem>
            {node.config.allowedTradeDirections && (
              <ConfigItem>
                <div className="label">Direction</div>
                <div className="value">{node.config.allowedTradeDirections}</div>
              </ConfigItem>
            )}
            {node.config.sharesPerTrade && (
              <ConfigItem>
                <div className="label">Shares Per Trade</div>
                <div className="value">{node.config.sharesPerTrade}</div>
              </ConfigItem>
            )}
            {node.config.contractsPerTrade && (
              <ConfigItem>
                <div className="label">Contracts Per Trade</div>
                <div className="value">{node.config.contractsPerTrade}</div>
              </ConfigItem>
            )}
            {node.config.lotSize && (
              <ConfigItem>
                <div className="label">Lot Size</div>
                <div className="value">{node.config.lotSize}</div>
              </ConfigItem>
            )}
            {node.config.optionType && (
              <ConfigItem>
                <div className="label">Option Type</div>
                <div className="value">{node.config.optionType}</div>
              </ConfigItem>
            )}
            {node.config.quantityPerTrade && (
              <ConfigItem>
                <div className="label">Quantity Per Trade</div>
                <div className="value">{node.config.quantityPerTrade}</div>
              </ConfigItem>
            )}
            {node.config.lotMode && (
              <ConfigItem>
                <div className="label">Lot Mode</div>
                <div className="value">{node.config.lotMode}</div>
              </ConfigItem>
            )}
            {node.config.leverage && (
              <ConfigItem>
                <div className="label">Leverage</div>
                <div className="value">{node.config.leverage}</div>
              </ConfigItem>
            )}
            {node.config.riskSource && (
              <ConfigItem>
                <div className="label">Risk Source</div>
                <div className="value">{node.config.riskSource}</div>
              </ConfigItem>
            )}
            {node.config.riskPercentage && (
              <ConfigItem>
                <div className="label">Risk %</div>
                <div className="value">{node.config.riskPercentage}%</div>
              </ConfigItem>
            )}
            {node.config.fixedCapital && (
              <ConfigItem>
                <div className="label">Fixed Capital</div>
                <div className="value">${node.config.fixedCapital}</div>
              </ConfigItem>
            )}
            {node.config.maxDollarRisk && (
              <ConfigItem>
                <div className="label">Max $ Risk</div>
                <div className="value">${node.config.maxDollarRisk}</div>
              </ConfigItem>
            )}
            {node.config.maxSpendingPerTrade && (
              <ConfigItem>
                <div className="label">Max Spending</div>
                <div className="value">${node.config.maxSpendingPerTrade}</div>
              </ConfigItem>
            )}
            {node.config.maxTotalSpendingPerDay && (
              <ConfigItem>
                <div className="label">Daily Limit</div>
                <div className="value">${node.config.maxTotalSpendingPerDay}</div>
              </ConfigItem>
            )}
          </>
        );
      default:
        return (
          <>
            {Object.entries(node.config)
              .filter(([key]) => !key.startsWith("_") && key !== "openingCommission" && key !== "closingCommission" &&
                !["pipSize", "pipValueInUsd", "swapFees", "contractMultiplier", "quantityContracts",
                  "pnlMultiplier", "tickValue", "contractType", "positionSize", "fundingRate",
                  "fundingTimestamp", "currentMarkPrice"].includes(key))
              .slice(0, 8) // Limit to first 8 fields to avoid overflow
              .map(([key, value]) => (
                <ConfigItem key={key}>
                  <div className="label">
                    {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                  </div>
                  <div className="value">
                    {typeof value === "object" ? JSON.stringify(value).slice(0, 50) : String(value).slice(0, 30)}
                  </div>
                </ConfigItem>
              ))}
          </>
        );
    }
  };

  return (
    <NodeWrapper
      isSelected={isSelected}
      isMultiColumn={isMultiColumnNode}
      onClick={onClick}
      drag
      dragMomentum={false}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDrag={handleDrag}
      style={{
        x,
        y,
        scale,
        opacity,
        zIndex: isDragging.current ? 1000 : 1,
      }}
      animate={{
        scale: isDragging.current ? 1.05 : 1,
        opacity: isDragging.current ? 0.8 : 1,
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
      }}
    >
      <NodeHeader>{node.type}</NodeHeader>
      <NodeBody>{renderConfigContent()}</NodeBody>
    </NodeWrapper>
  );
};

export default Node;
